import { Component } from 'react'
import { observable, transaction } from 'mobx'
import { observer } from 'mobx-react'
import { browserHistory } from 'react-router'
import { CSSTransitionGroup } from 'react-transition-group'

import * as h from 'h'
import { debounce } from 'utils'

import Panel from './Panel'

import stylesPanel from './styles-panel.scss'
import styles from './styles-researchunleashed.scss'

const MobileBlurbsContainer = () => (
  h.ul(h.ca(styles.mobileBlurbsContainer),
    window.researchUnleashedFactsInit.map(({ id, desc, color }) =>
      h.li({ key: id, style: { backgroundColor: color } },
        h.raw({ content: desc })))
  )
)

@observer
class BlurbsContainer extends Component {
  static defaultProps = {
    freq: 5000,
    startingPosition: 0,
    numberOfColumns: 3,
    blurbsPerColumn: 3,
    cycleAnimationDuration: 300
  }

  get blurbsInit () {
    return window.researchUnleashedFactsInit
  }

  componentWillMount () {
    this.columns = [0, 1, 2].map(i => observable({
      blurbs: this.blurbsInit.slice(3 * i, 3 * (i + 1))
        .map(b => h.h(FactBlurb, { key: b.desc, ...b })),
      position: this.props.startingPosition,
      willCycleIn: false
    }))

    // Store timers to invalidate on unmount
    this.timers = []

    this.remainingNews = window.newsTickerStoriesInit
      .map(({ id, title, imageUrl }) => ({ id, key: id, desc: title, image: imageUrl }))
    this.remainingTweets = window.twitterTickerStoriesInit
      .map(({ id, title, avatar, url }) => ({ key: id, desc: title, image: avatar, url }))

    this.checkVisibilityDebounced = debounce(::this.checkVisibility, 300)
  }

  componentDidMount () {
    window.addEventListener('scroll', this.checkVisibilityDebounced, true)
  }

  componentWillUnmount () {
    window.removeEventListener('scroll', this.checkVisibilityDebounced, true)
    this.stopAutoCycling()
  }

  checkVisibility () {
    if (this.refs.container.getBoundingClientRect().top + 50 < window.innerHeight) {
      this.startAutoCycling()
      window.removeEventListener('scroll', this.checkVisibilityDebounced, true)
    }
  }

  startAutoCycling () {
    const { freq, numberOfColumns } = this.props

    // Timer interval should be slightly more than the auto cycle time to ensure
    // a gap exists between the end of a cycle and the start of another.
    const every = freq + 100

    const firstCycleDuration = 500
    this.cycleColumn(2, { delay: firstCycleDuration })

    setTimeout(() => {
      this.cycleColumn(1)
      this.autoCycleTimerId = setInterval(() => {
        const randomColumn = Math.floor(Math.random() * numberOfColumns)
        this.cycleColumn(randomColumn)
      }, every)
    }, firstCycleDuration)
  }

  stopAutoCycling () {
    this.timers.map(timer => clearTimeout(timer))
    clearInterval(this.autoCycleTimerId)
    for (let column of this.columns) {
      column.willCycleIn = false
    }
  }

  cycleColumn (i, opts = {}) {
    const delay = opts.delay !== undefined ? opts.delay : 5000
    const direction = opts.direction || 'down'

    this.columns[i].willCycleIn = delay
    this.timers.push(setTimeout(() => {
      this.cycleColumnSync(i, { direction })
      this.columns[i].willCycleIn = false
    }, delay))
  }

  getNextBlurbEl () {
    if (this.remainingTweets.length === 0) {
      return h.h(NewsBlurb, this.remainingNews.pop())
    } else if (this.remainingNews.length === 0) {
      return h.h(TwitterBlurb, this.remainingTweets.pop())
    }

    return Math.random() >= 0.5
      ? h.h(NewsBlurb, this.remainingNews.pop())
      : h.h(TwitterBlurb, this.remainingTweets.pop())
  }

  shouldAddBlurbToColumn (i) {
    return this.columns[i].blurbs.length < 8 &&
      this.remainingTweets.length !== 0 &&
      this.remainingNews.length !== 0
  }

  cycleColumnSync (columnIndex, { direction }) {
    if (!this.shouldAddBlurbToColumn(columnIndex)) {
      this.columns[columnIndex].position += direction === 'up' ? 1 : -1
      return
    }

    transaction(() => {
      const newItem = this.getNextBlurbEl()
      if (direction === 'up') {
        this.columns[columnIndex].blurbs.push(newItem)
        this.columns[columnIndex].position += 1
      } else {
        this.columns[columnIndex].blurbs.unshift(newItem)
      }
    })
  }

  onClick (...args) {
    this.cycleColumnSync(...args)
    this.stopAutoCycling()
  }

  render () {
    return h.div({ ...h.ca(styles.blurbsContainer), ref: 'container' },
      this.columns.map((column, columnIndex) => (
        h.h(BlurbColumn, {
          key: columnIndex,
          onClickUp: () => this.onClick(columnIndex, { direction: 'up' }),
          onClickDown: () => this.onClick(columnIndex, { direction: 'down' }),
          column
        })
      ))
    )
  }
}

@observer
class BlurbColumn extends Component {
  getRelativePosition (blurbs, position) {
    const index = position % blurbs.length
    return index >= 0
      ? index
      : blurbs.length + index
  }

  getBlurbsFromPosition (blurbs, position) {
    let start = this.getRelativePosition(blurbs, position)

    // TODO: This can be less hardcoded
    return [
      blurbs[start],
      blurbs[(start + 1) % blurbs.length],
      blurbs[(start + 2) % blurbs.length]
    ]
  }

  render () {
    const { column, onClickUp, onClickDown } = this.props

    return h.div(h.ca(styles.blurbColumn),
      h.icon({
        ...h.ca(styles.shiftButton),
        name: 'angle-up',
        onClick: onClickUp
      }),
      column.willCycleIn && h.h(WillCycleIndicator, { duration: column.willCycleIn }),
      h.h(CSSTransitionGroup, {
        ...h.ca(styles.transitionContainer),
        transitionName: {
          enter: styles['blurb-enter'],
          enterActive: styles['blurb-enter-active'],
          leave: styles['blurb-leave']
        },
        transitionEnterTimeout: 300,
        transitionLeaveTimeout: 300,
        children: this.getBlurbsFromPosition(column.blurbs, column.position)
      }),
      h.icon({
        ...h.ca(styles.shiftButton),
        name: 'angle-down',
        onClick: onClickDown
      })
    )
  }
}

const FactBlurb = ({ desc: content, color }) => (
  h.div({ style: { backgroundColor: color }, ...h.ca(styles.blurb, styles.factBlurb) },
    h.raw({ content })
  )
)

const TwitterBlurb = ({ desc: content, url: href, image }) => (
  h.a({
    ...h.ca(styles.blurb, styles.twitterBlurb),
    href,
    target: '_blank',
    rel: 'noopener noreferrer',
    children: [
      h.div(h.ca(styles.avatarContainer),
        h.img({ src: image, name: '' })),
      h.p(null, content)
    ]
  })
)

const NewsBlurb = ({ id, desc: content, image }) => (
  h.div({
    ...h.ca(styles.blurb, styles.newsBlurb),
    style: { backgroundImage: `url(${image})` },
    onClick: () => {
      browserHistory.push('/news')
      // Wait until after the landing slide has pulled up before bringing in the
      // story popout.
      setTimeout(() => {
        browserHistory.push(`/news/${id}/${encodeURIComponent(content)}`)
      }, 1200)
    },
    children: h.raw({ content })
  })
)

const WillCycleIndicator = ({ duration }) => (
  h.div(h.ca(styles.willCycleIndicator),
    h.div({
      ...h.ca(styles.willCycleIndicatorInner),
      style: { animationDuration: `${duration}ms` }
    })
  ))

@observer(['UiStore'])
export default class ResearchUnleashedPanel extends Component {
  render () {
    const { UiStore, onArrowClick } = this.props
    const backgroundImage = 'url(/landingimage/researchunleashed)'

    return h.h(Panel, { onArrowClick, backgroundImage },
      h.div(h.ca(stylesPanel.standardContent, styles.container),
        h.h1(h.ca(stylesPanel.standardContentTitle, styles.title),
          'Research ', h.strong(null, 'Unleashed')),
        UiStore.windowWidth > '500'
          ? h.h(BlurbsContainer, { blurbs: window.researchUnleashedFactsInit })
          : h.h(MobileBlurbsContainer)
      ))
  }
}



// WEBPACK FOOTER //
// ./resources/assets/js/containers/Landing/ResearchUnleashedPanel.js